import { getMaxAttemptsForLevel, getWordLengthForLevel } from './game/GameLogic';
import { getWordForDay } from '@/lib/wordLibrary';
import * as CookieManager from '@/lib/managers/CookieManager';
import { getEffectiveDate, formatDate } from '@/lib/dateUtils';
import { LetterBox } from './LetterBox';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';
import { X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

interface GuessHistoryProps {
  guessHistory: {
    level: number;
    word: string;
    guesses: { letter: string; status: string }[][];
  }[];
  failedAtLevel?: number;
  showGuessHistory: boolean;
  onClose?: () => void;
}

export const GuessHistory = ({ 
  guessHistory: propGuessHistory, 
  failedAtLevel: propFailedAtLevel,
  showGuessHistory,
  onClose
}: GuessHistoryProps) => {
  const effectiveDate = getEffectiveDate();
  const dailyWords = getWordForDay(effectiveDate);
  const today = formatDate(effectiveDate);

  // Use mode-aware storage for all game state access
  const savedGuessHistory = modeAwareStorage.getCookie('guess_history');
  const savedFailedLevel = modeAwareStorage.getCookie('failed_level');
  const savedGameStatus = modeAwareStorage.getGameStatus();
  const savedDate = modeAwareStorage.getCookie('game_persistence_date');

  const shouldUseSavedState = savedDate === today;

  const processGuessHistory = (history) => {
    if (!Array.isArray(history)) return [];
    
    return history
      .filter(h => h && h.level && h.word && Array.isArray(h.guesses))
      .map(levelHistory => {
        const uniqueGuesses = [];
        const seenGuesses = new Set();
        
        if (levelHistory.guesses && Array.isArray(levelHistory.guesses)) {
          for (const guess of levelHistory.guesses) {
            const guessString = JSON.stringify(guess);
            if (!seenGuesses.has(guessString)) {
              seenGuesses.add(guessString);
              uniqueGuesses.push(guess);
            }
          }
        }
        
        return {
          ...levelHistory,
          guesses: uniqueGuesses
        };
      });
  };

  const unfilteredGuessHistory = shouldUseSavedState && savedGuessHistory ? 
    processGuessHistory(JSON.parse(savedGuessHistory)) : 
    processGuessHistory(propGuessHistory);

  const effectiveGuessHistory = Array.isArray(unfilteredGuessHistory)
    ? unfilteredGuessHistory.filter(h => dailyWords[h.level - 1] === h.word)
    : [];

  const effectiveFailedLevel = shouldUseSavedState && savedGameStatus === 'lost' ? 
    (savedFailedLevel ? parseInt(savedFailedLevel) : propFailedAtLevel) : 
    propFailedAtLevel;
  const getStatusClass = (status: string): "correct" | "wrong-position" | "incorrect" | "empty" | "completed" => {
    if (status === "misplaced") return "wrong-position";
    return status as "correct" | "wrong-position" | "incorrect" | "empty" | "completed";
  };

  const allLevels = [1, 2, 3, 4];

  const shouldShowAnswer = (level: number, levelHistory: typeof effectiveGuessHistory[0] | undefined) => {
    console.log('shouldShowAnswer check:', {
      level,
      effectiveFailedLevel,
      levelHistory: levelHistory ? {
        guessCount: levelHistory?.guesses.length,
        lastGuessCorrect: levelHistory?.guesses.length ? 
          levelHistory.guesses[levelHistory.guesses.length - 1].every(letter => letter.status === "correct") : false
      } : 'no history',
      shouldShow: effectiveFailedLevel !== undefined && level >= effectiveFailedLevel
    });
    
    if (effectiveFailedLevel !== undefined && level >= effectiveFailedLevel) {
      return true;
    }
    
    if (!levelHistory || !levelHistory.guesses || levelHistory.guesses.length === 0) {
      return true;
    }
    
    if (levelHistory.guesses.length > 0) {
      const lastGuess = levelHistory.guesses[levelHistory.guesses.length - 1];
      const wasLevelCompleted = lastGuess.every(letter => letter.status === "correct");
      
      if (!wasLevelCompleted) {
        return true;
      }
    }
    
    return false;
  };

  console.log('GuessHistory render:', {
    savedGuessHistory: effectiveGuessHistory,
    savedFailedLevel: effectiveFailedLevel,
    savedGameStatus,
    shouldUseSavedState,
    today,
    savedDate,
    effectiveDate: effectiveDate.toISOString(),
    dailyWords
  });

  return (
    <div className="mt-4">
      {showGuessHistory && (
        <div className="mt-4 w-full max-w-xs mx-auto overflow-hidden rounded-xl shadow-xl relative" style={{ outline: "6px solid #454545" }}>
          <div className="absolute inset-0 overflow-hidden rounded-xl">
            <div className="absolute inset-0 opacity-70 rounded-xl">
              <div 
                className="absolute inset-0 rounded-xl"
                style={{
                  backgroundImage: `linear-gradient(to top, #8E9196, transparent)`
                }}
              />
            </div>
            <div className="absolute inset-0 bg-gray-900/40 rounded-xl" />
            
            <div 
              className="absolute inset-0 pattern-dots-fallback rounded-xl" 
              style={{ 
                backgroundImage: 'radial-gradient(circle, #C8C8C9 1px, transparent 1px)',
                backgroundSize: '12px 12px',
                backgroundPosition: '0 0',
                opacity: 0.2,
                zIndex: 2
              }}
            ></div>
            
            <div 
              className="absolute inset-0 pattern-dots-fallback rounded-xl" 
              style={{ 
                backgroundImage: 'radial-gradient(circle, #C8C8C9 1px, transparent 1px)',
                backgroundSize: '12px 12px',
                backgroundPosition: '6px 6px',
                opacity: 0.2,
                zIndex: 2
              }}
            ></div>
          </div>
          
          <div className="relative z-10 p-3 sm:p-4 rounded-xl">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wide">
                Guesses & Answers
              </h3>
              {onClose && (
                <button
                  type="button"
                  onClick={() => {
                    trackEvent('toggle_guess_history', { action: 'close', source: 'panel_x' });
                    onClose();
                  }}
                  aria-label="Close guesses and answers"
                  className="-mr-1 -mt-1 p-1.5 rounded-full bg-black text-gray-400 hover:text-white hover:bg-gray-900 border border-gray-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <Carousel className="w-full guess-history-carousel">
              <CarouselContent>
                {allLevels.map((level) => {
                  const wordLength = getWordLengthForLevel(level);
                  const levelHistory = effectiveGuessHistory.find(h => h.level === level);
                  const maxAttempts = getMaxAttemptsForLevel(wordLength);
                  const answer = dailyWords[level - 1];
                  const showAnswer = shouldShowAnswer(level, levelHistory);

                  console.log(`Level ${level} render check:`, {
                    wordLength, 
                    hasLevelHistory: !!levelHistory, 
                    maxAttempts,
                    answer,
                    showAnswer
                  });

                  return (
                    <CarouselItem key={level}>
                      <div className="p-2">
                        <div className="mb-2 text-center">
                          <div className="text-sm font-bold text-gray-400">
                            Level {level}
                          </div>
                          <div className="text-xs text-gray-500">
                            ({maxAttempts} attempts)
                          </div>
                        </div>
                        <div className="space-y-1 flex flex-col items-center">
                          {levelHistory?.guesses.map((guess, guessIndex) => (
                            <div key={guessIndex} className="flex gap-1">
                              {guess.map((letter, letterIndex) => (
                                <LetterBox
                                  key={letterIndex}
                                  letter={letter.letter}
                                  status={getStatusClass(letter.status)}
                                  isCompact={true}
                                />
                              ))}
                            </div>
                          ))}

                          {levelHistory && levelHistory.guesses.length < maxAttempts && (
                            Array.from({ length: maxAttempts - levelHistory.guesses.length }).map((_, index) => (
                              <div key={`empty-${index}`} className="flex gap-1">
                                {Array.from({ length: wordLength }).map((_, letterIndex) => (
                                  <LetterBox
                                    key={letterIndex}
                                    status="empty"
                                    isCompact={true}
                                  />
                                ))}
                              </div>
                            ))
                          )}

                          {!levelHistory && (
                            Array.from({ length: maxAttempts }).map((_, index) => (
                              <div key={`empty-${index}`} className="flex gap-1">
                                {Array.from({ length: wordLength }).map((_, letterIndex) => (
                                  <LetterBox
                                    key={letterIndex}
                                    status="empty"
                                    isCompact={true}
                                  />
                                ))}
                              </div>
                            ))
                          )}

                          {showAnswer && answer && (
                            <div className="flex flex-col items-center mt-2">
                              <div className="text-xs text-gray-400 mb-1">Answer:</div>
                              <div className="flex gap-1 z-50 relative answer-container">
                                {Array.from({ length: wordLength }).map((_, index) => (
                                  <LetterBox
                                    key={index}
                                    letter={answer[index] || ' '}
                                    status="correct"
                                    isCompact={true}
                                    isAnswer={true}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <div className="flex justify-center gap-2 mt-2">
                <CarouselPrevious className="relative left-0 top-auto translate-y-0" />
                <CarouselNext className="relative right-0 top-auto translate-y-0" />
              </div>
            </Carousel>
          </div>
        </div>
      )}
    </div>
  );
};
